import { otpSignIn, sendOtpRequest } from '@/lib/auth.client';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

interface CheckEmailResponse {
  isBlocked?: boolean;
  error?: string;
}

interface SendOtpResponse {
  error?: string;
  message?: string;
}

interface VerifyOtpResponse {
  error?: string;
  user?: unknown;
  token?: string;
}

const RESEND_COOLDOWN_SECONDS = 30;

/** Shown when OTP send or verify returns 429 (rate limit). */
const RATE_LIMIT_MESSAGE = 'Too many attempts. Please try again after a few minutes.';

type Status =
  | 'idle' // Initial state on landing page
  | 'sendingOtp' // Email submitted, waiting for OTP to be sent
  | 'emailError' // Error during email check or OTP sending
  | 'awaitingOtp' // OTP screen is visible, waiting for user input
  | 'verifyingOtp' // OTP submitted, waiting for verification
  | 'otpError' // Error during OTP verification
  | 'verified'; // OTP verified successfully

interface State {
  status: Status;
  email: string;
  otp: string;
  error: string;
  canResend: boolean;
  resendCountdown: number;
}

const initialState: State = {
  status: 'idle',
  email: '',
  otp: '',
  error: '',
  canResend: false,
  resendCountdown: RESEND_COOLDOWN_SECONDS,
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

type Action =
  | { type: 'SET_EMAIL'; payload: string }
  | { type: 'SET_OTP'; payload: string }
  | { type: 'SUBMIT_EMAIL' }
  | { type: 'SEND_OTP_SUCCESS' }
  | { type: 'FAILURE'; payload: { error: string; on: 'email' | 'otp' } }
  | { type: 'VERIFY_OTP' }
  | { type: 'VERIFY_SUCCESS' }
  | { type: 'RESEND' }
  | { type: 'BACK' }
  | { type: 'TICK' }
  | { type: 'COUNTDOWN_FINISH' };

function emailOtpReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_EMAIL': {
      // console.log('SET_EMAIL', action.payload);
      return { ...state, email: action.payload, status: 'idle', error: '' };
    }
    case 'SET_OTP': {
      return {
        ...state,
        otp: action.payload,
        status: state.status === 'otpError' ? 'awaitingOtp' : state.status,
        error: '',
      };
    }
    case 'SUBMIT_EMAIL': {
      return { ...state, status: 'sendingOtp', error: '' };
    }
    case 'SEND_OTP_SUCCESS': {
      return { ...initialState, email: state.email, status: 'awaitingOtp' };
    }
    case 'FAILURE': {
      return {
        ...state,
        status: action.payload.on === 'email' ? 'emailError' : 'otpError',
        error: action.payload.error,
      };
    }
    case 'VERIFY_OTP': {
      return { ...state, status: 'verifyingOtp', error: '' };
    }
    case 'VERIFY_SUCCESS': {
      return { ...state, status: 'verified', error: '' };
    }
    case 'RESEND': {
      return { ...state, canResend: false, resendCountdown: RESEND_COOLDOWN_SECONDS, status: 'awaitingOtp', error: '' };
    }
    case 'BACK': {
      return { ...initialState, email: state.email };
    }
    case 'TICK': {
      if (state.resendCountdown <= 0) {
        return state;
      }

      return { ...state, resendCountdown: state.resendCountdown - 1 };
    }
    case 'COUNTDOWN_FINISH': {
      return { ...state, canResend: true, resendCountdown: 0 };
    }
    default: {
      return state;
    }
  }
}

export function useEmailOtpFlow() {
  const [state, dispatch] = useReducer(emailOtpReducer, initialState);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const startCountdown = useCallback(() => {
    clearCountdown();
    countdownTimerRef.current = setInterval(() => {
      dispatch({ type: 'TICK' });
    }, 1000);
  }, []);

  useEffect(() => {
    if (state.resendCountdown <= 0) {
      clearCountdown();

      if (state.status === 'awaitingOtp' || state.status === 'otpError') {
        dispatch({ type: 'COUNTDOWN_FINISH' });
      }
    }
  }, [state.resendCountdown, state.status]);

  // Cleanup timer on unmount
  useEffect(() => {
    return clearCountdown;
  }, []);

  const normalizeResponse = async <T>(response: Response): Promise<T> => {
    try {
      return (await response.json()) as T;
    } catch {
      return {} as T;
    }
  };

  const checkEmail = useCallback(async (emailToCheck: string) => {
    const response = await fetch('/api/check-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: emailToCheck }),
    });

    const data = await normalizeResponse<CheckEmailResponse>(response);

    if (!response.ok || data.isBlocked) {
      const message =
        data.error ||
        (data.isBlocked
          ? 'Disposable email addresses are not allowed.'
          : 'Failed to validate email. Please try again.');
      throw new Error(message);
    }
  }, []);

  // const sendOtp = useCallback(async (emailToSend: string) => {
  //   const response = await fetch('/api/otp/send', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({ email: emailToSend }),
  //   });

  //   const data = await normalizeResponse<SendOtpResponse>(response);

  //   if (!response.ok) {
  //     const message = response.status === 429 ? RATE_LIMIT_MESSAGE : data.error || 'Failed to send OTP';
  //     throw new Error(message);
  //   }
  // }, []);

  const checkOtpRateLimit = useCallback(async (emailToCheck: string) => {
    const response = await fetch('/api/otp/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: emailToCheck, otp: 'ratelimit-check', checkOnly: true }),
    });

    const data = await normalizeResponse<SendOtpResponse>(response);

    if (!response.ok) {
      const message = response.status === 429 ? RATE_LIMIT_MESSAGE : data.error || 'Failed to send OTP';
      throw new Error(message);
    }
  }, []);

  const submitEmail = useCallback(async () => {
    const normalizedEmail = normalizeEmail(state.email);

    if (!normalizedEmail) {
      dispatch({ type: 'FAILURE', payload: { error: 'Please enter an email', on: 'email' } });
      return;
    }

    dispatch({ type: 'SUBMIT_EMAIL' });

    try {
      await checkEmail(normalizedEmail);

      // await checkOtpRateLimit(normalizedEmail);

      const { error: otpError } = await sendOtpRequest(normalizedEmail);

      if (otpError) {
        throw new Error(otpError.message || 'Failed to send OTP');
      }

      dispatch({ type: 'SEND_OTP_SUCCESS' });
      startCountdown();
    } catch (error) {
      console.log('[otp] error:', error);

      const message = error instanceof Error ? error.message : 'Failed to validate email';
      dispatch({ type: 'FAILURE', payload: { error: message, on: 'email' } });
    }
  }, [state.email, checkEmail, sendOtpRequest, startCountdown]);

  const resendOtp = useCallback(async () => {
    const normalizedEmail = normalizeEmail(state.email);

    if (!state.canResend) {
      return;
    }

    dispatch({ type: 'RESEND' });

    try {
      // await checkOtpRateLimit(normalizedEmail);

      const { error: otpError } = await sendOtpRequest(normalizedEmail);

      if (otpError) {
        throw new Error(otpError.message || 'Failed to resend OTP');
      }

      startCountdown();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resend OTP';
      dispatch({ type: 'FAILURE', payload: { error: message, on: 'otp' } });
    }
  }, [state.canResend, state.email, sendOtpRequest, startCountdown]);

  const verifyOtp = useCallback(
    async (otpValue?: string) => {
      const otpToVerify = otpValue || state.otp;

      if (state.status === 'verifyingOtp') {
        return;
      }

      if (!otpToVerify || otpToVerify.length !== 6) {
        dispatch({ type: 'FAILURE', payload: { error: 'Please enter a 6-digit OTP', on: 'otp' } });
        return;
      }

      dispatch({ type: 'VERIFY_OTP' });

      try {
        const normalizedEmail = normalizeEmail(state.email);

        // const response = await fetch('/api/otp/verify', {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //   },
        //   body: JSON.stringify({ email: normalizedEmail, otp: otpToVerify }),
        // });

        const { error } = await otpSignIn(normalizedEmail, otpToVerify);

        if (error) {
          const serverMessage = error.message || 'Failed to verify OTP';
          dispatch({ type: 'FAILURE', payload: { error: serverMessage, on: 'otp' } });
          return;
        }

        dispatch({ type: 'VERIFY_SUCCESS' });
        window.location.href = '/home';
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to verify OTP';
        dispatch({ type: 'FAILURE', payload: { error: message, on: 'otp' } });
      }
    },
    [state.email, state.otp, state.status],
  );

  const backToLanding = useCallback(() => {
    clearCountdown();
    dispatch({ type: 'BACK' });
  }, []);

  // --- Mapped state for components ---
  // This preserves the hook's public API for the components.
  const view = ['awaitingOtp', 'verifyingOtp', 'otpError', 'verified'].includes(state.status) ? 'email' : 'landing';

  const isSendingOtp = state.status === 'sendingOtp';

  type OtpState = 'idle' | 'verifying' | 'verified' | 'failed';

  const otpState: OtpState = useMemo(() => {
    switch (state.status) {
      case 'verifyingOtp': {
        return 'verifying';
      }
      case 'verified': {
        return 'verified';
      }
      case 'otpError': {
        return 'failed';
      }
      default: {
        return 'idle';
      }
    }
  }, [state.status]);

  const primaryCtaLabel = state.status === 'sendingOtp' ? 'Sending code...' : 'Send me a code';
  const isPrimaryBusy = state.status === 'sendingOtp';
  const emailError = state.status === 'emailError' ? state.error : '';
  const otpError = state.status === 'otpError' ? state.error : '';

  const setEmail = useCallback((email: string) => dispatch({ type: 'SET_EMAIL', payload: email }), []);
  const setOtp = useCallback((otp: string) => dispatch({ type: 'SET_OTP', payload: otp }), []);

  /*
    Manual test checklist:
    - Email check: blocked/400/500 and silent 200 with {} → error shown, stay on landing.
    - OTP send: 400/500/offline/silent 200 with {} → error shown, spinner stops, stay on landing.
    - Verify: invalid length → inline error; 400/500/silent 200 with {} → failed state; success redirects.
    - Resend: success resets countdown; failure keeps countdown and shows error; offline shows error.
    - Abort: navigate away mid-request → no state update warnings or errors in console.
  */

  return {
    email: state.email,
    setEmail,
    view,
    primaryCtaLabel,
    isPrimaryBusy,
    submitEmail,
    isSendingOtp,
    emailError,
    otp: state.otp,
    setOtp,
    otpState,
    otpError,
    verifyOtp,
    resendOtp,
    canResend: state.canResend,
    resendCountdown: state.resendCountdown,
    backToLanding,
  };
}
